import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import TableContainer from "../../../Components/Common/TableContainer";
import axios from "../../../utills/api"
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import InvoiceDownload from "./InvoiceDownload";

const BASE_API = "/checkout/checkout";

const ManageCheckout = () => {
  document.title = "HMS | Checkout";

  // State
  const [bookings, setBookings] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [cashModal, setCashModal] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Toggle modals
  const toggleViewModal = () => {
    setViewModal((v) => !v);
    if (viewModal) setViewData(null); // clear when closing
  };

  const toggleCashModal = () => setCashModal((v) => !v);

  // Fetch all bookings
  const fetchBookings = async () => {
    try {
      const res = await axios.get(BASE_API);
      const data = Array.isArray(res.data) ? res.data : [];

      // Normalize extensionAmount as number (safety)
      const normalized = data.map((b) => ({
        ...b,
        extensionAmount: Number(b.extensionAmount || 0),
      }));

      setBookings(normalized);
    } catch (error) {
      toast.error("Failed to fetch checkouts!");
      console.error("fetchBookings error:", error);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // View booking details
  const handleViewClick = async (bookingId) => {
    try {
      const url = `/checkout/checkout/${encodeURIComponent(bookingId)}`;
      const res = await axios.get(url);
      const apiData = res.data || {};

      if (!apiData || !apiData.booking) {
        toast.error("Booking details not available!");
        return;
      }

      const bookingData = apiData.booking;

      let totalExtensionAmount = Number(bookingData.extensionAmount || 0);

      const bookingDetails = {
        bookingId: bookingData.bookingId || bookingData._id,
        name: bookingData.name || "-",
        mobile: bookingData.mobile || "-",
        roomNumber: bookingData.assignRoom || bookingData.roomNumber || "-",
        roomType: bookingData.roomType || "-",
        checkIn: bookingData.checkIn || null,
        checkOut: bookingData.checkOut || null,

        originalBookingAmount: Number(bookingData.payment || 0),

        charges: {
          ...apiData.charges,
          extension: totalExtensionAmount,
        },

        services: apiData.services || {
          foodOrders: [],
          spaOrders: [],
          wellnessOrders: [],
          taxiOrders: [],
        },

        payments: apiData.payments || [],

        totalServices: apiData.totalServices || 0,
        totalPaid: apiData.totalPaid || 0,
        totalAmount: apiData.totalAmount || 0,
        balanceDue: apiData.balanceDue || 0,

        status: bookingData.statusPayment || bookingData.status || "-",

        extensionAmount: totalExtensionAmount,
      };

      setViewData(bookingDetails);
      setViewModal(true);
    } catch (error) {
      console.error("Error fetching booking details:", error);
      toast.error("Failed to fetch booking details.");
    }
  };

  // Handle cash received
  const handleCashReceived = async () => {
    if (!viewData) return;

    try {
      const res = await axios.post(
        `/checkout/receive-cash`,
        { bookingId: viewData.bookingId }
      );

      if (res.data.success) {
        toast.success("Cash received! Checkout completed.");
        setCashModal(false);
        toggleViewModal();
        fetchBookings();
      } else {
        toast.error("Failed to mark as paid!");
      }
    } catch (err) {
      console.error("Cash receive error:", err);
      toast.error("Server error. Try again.");
    }
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case "Check-In":
        return <span className="badge text-uppercase bg-success-subtle text-success">{status}</span>;
      case "Ongoing":
        return <span className="badge text-uppercase bg-secondary-subtle text-secondary">{status}</span>;
      case "Check-Out":
        return <span className="badge text-uppercase bg-primary-subtle text-primary">{status}</span>;
      case "Pending":
        return <span className="badge text-uppercase bg-warning-subtle text-warning">{status}</span>;
      case "Cancelled":
        return <span className="badge text-uppercase bg-danger-subtle text-danger">{status}</span>;
      default:
        return <span className="badge text-uppercase bg-warning-subtle text-warning">{status}</span>;
    }
  };

  // Columns
  const columns = useMemo(() => [
    { header: "ID", accessorKey: "bookingId" },
    { header: "Name", accessorKey: "name" },
    { header: "Room Number", accessorKey: "roomNumber" },
    {
      header: "Check-In Date",
      accessorKey: "checkIn",
      cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "-"),
    },
    {
      header: "Checkout Date",
      accessorKey: "checkOut",
      cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "-"),
    },
    {
      header: "Extension Amount",
      accessorKey: "extensionAmount",
      cell: ({ cell }) => `₹${(Number(cell.getValue()) || 0).toLocaleString()}`,
    },
    {
      header: "Balance Due",
      accessorKey: "balanceDue",
      cell: ({ cell }) => `₹${(Number(cell.getValue()) || 0).toLocaleString()}`,
    },
    {
      header: "Status Payment",
      accessorKey: "statusPayment",
      cell: ({ row }) => {
        const balance = Math.max(
          0,
          Number(row.original.balanceDue || 0)
        );
        let label = "Unpaid";
        let color = "bg-danger-subtle text-danger";
        if (balance === 0) {
          label = "Paid";
          color = "bg-success-subtle text-success";
        } else if (balance > 0 && row.original.statusPayment === "Paid") {
          label = "Partial";
          color = "bg-warning-subtle text-warning";
        }
        return <span className={`badge ${color} text-uppercase`}>{label}</span>;
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ cell }) => renderStatusBadge(cell.getValue()),
    },
    {
      header: "Action",
      cell: ({ row }) => (
        <Button color="primary" size="sm" onClick={() => handleViewClick(row.original.bookingId)}>
          View
        </Button>
      ),
    },
  ], []);

  // Filtered data
  const filteredBookings = useMemo(() => {
    let filtered = bookings;
    if (searchTerm) {
      filtered = filtered.filter(
        (b) =>
          b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.bookingId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }
    return filtered;
  }, [bookings, searchTerm, statusFilter]);

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Manage Checkout" pageTitle="Checkouts" />
          <Row>
            <Col>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Checkouts</h5>
                </div>
                <div className="card-body">
                  <Row className="g-2 mb-3">
                    <Col lg="3">
                      <Input
                        type="text"
                        placeholder="Search by Name or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col lg="3">
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid</option>
                      </Input>
                    </Col>
                  </Row>
                  <div className="table-responsive">
                    <TableContainer
                      columns={columns}
                      data={filteredBookings}
                      customPageSize={10}
                      isGlobalFilter={false}
                      isPagination
                      className="text-nowrap"
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* View Modal */}
      <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered>
        <ModalHeader toggle={toggleViewModal}>Booking Details</ModalHeader>
        <ModalBody>
          {viewData ? (
            <div>
              <h5>Guest: {viewData.name || "-"}</h5>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Room Number</th>
                    <th>Check-In</th>
                    <th>Check-Out</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{viewData.roomNumber || "-"}</td>
                    <td>{viewData.checkIn ? new Date(viewData.checkIn).toLocaleString() : "-"}</td>
                    <td>{viewData.checkOut ? new Date(viewData.checkOut).toLocaleString() : "-"}</td>
                  </tr>
                </tbody>
              </table>

              <h6>Booking Amount</h6>
              <div>
                ₹{(viewData.charges?.booking || 0).toLocaleString()} {viewData.charges?.booking > 0 ? "(Unpaid)" : "(Already Paid)"}
              </div>

              <h6 className="mt-3">Extension Amount (Unpaid)</h6>
              <div>
                ₹{(viewData.extensionAmount || 0).toLocaleString()}{" "}
                {viewData.extensionAmount > 0 ? "(Unpaid)" : "(No Extension)"}
              </div>

              {/* 🥘 FOOD ORDERS */}
              <h6 className="mt-3">Food Orders</h6>
              {(viewData.services?.foodOrders || []).length > 0 ? (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Qty</th>
                      <th>Date & Time</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.services.foodOrders.map((f, idx) => (
                      <tr key={f._id || f.orderId || idx}>
                        <td>{(f.items || []).map((i) => i.name).join(", ")}</td>
                        <td>{(f.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0)}</td>
                        <td>{f.date ? new Date(f.date).toLocaleString() : "-"}</td>
                        <td>₹{(f.finalAmount || 0).toLocaleString()}</td>
                        <td>₹{(f.discount || 0).toLocaleString()}</td>
                        <td>₹{((f.finalAmount || 0) - (f.discount || 0)).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>None</p>
              )}

              {/* 💆 SPA SERVICES */}
              <h6 className="mt-3">Spa Services</h6>
              {(viewData.services?.spaOrders || []).length > 0 ? (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Date & Time</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.services.spaOrders.map((s, idx) => (
                      <tr key={s._id || idx}>
                        <td>{s.serviceName || s.name || "-"}</td>
                        <td>{s.date ? new Date(s.date).toLocaleString() : "-"}</td>
                        <td>₹{(s.price || 0).toLocaleString()}</td>
                        <td>₹{(s.discount || 0).toLocaleString()}</td>
                        <td>₹{((s.price || 0) - (s.discount || 0)).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>None</p>
              )}

              {/* 🧘 WELLNESS SERVICES */}
              <h6 className="mt-3">Wellness Services</h6>
              {(viewData.services?.wellnessOrders || []).length > 0 ? (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Date & Time</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.services.wellnessOrders.map((w, idx) => (
                      <tr key={w._id || idx}>
                        <td>{w.serviceName || w.name || "-"}</td>
                        <td>{w.date ? new Date(w.date).toLocaleString() : "-"}</td>
                        <td>₹{(w.price || 0).toLocaleString()}</td>
                        <td>₹{(w.discount || 0).toLocaleString()}</td>
                        <td>₹{((w.price || 0) - (w.discount || 0)).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>None</p>
              )}

              {/* 🚖 TAXI SERVICES */}
              <h6 className="mt-3">Taxi Services</h6>
              {(viewData.services?.taxiOrders || []).length > 0 ? (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Pickup</th>
                      <th>Drop</th>
                      <th>Date</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.services.taxiOrders.map((t, idx) => (
                      <tr key={t._id || idx}>
                        <td>{t.pickup || "-"}</td>
                        <td>{t.drop || "-"}</td>
                        <td>{t.date ? new Date(t.date).toLocaleString() : "-"}</td>
                        <td>₹{(t.price || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>None</p>
              )}


              <h5 className="mt-3">Summary</h5>
              <p>Total Services: ₹{(viewData.totalServices || 0).toLocaleString()}</p>
              <p>Total Amount: ₹{(viewData.totalAmount || 0).toLocaleString()}</p>
              <p>Total Paid: ₹{(viewData.totalPaid || 0).toLocaleString()}</p>
              <h5>Balance Due: ₹{((viewData.balanceDue || 0)).toLocaleString()}</h5>

              {/* Action Buttons */}
              {/* Action Buttons */}
              <div className="d-flex justify-content-end mt-3 gap-2">

                {(() => {
                  const bookingAmount = Number(viewData.charges?.booking || 0);
                  const extensionAmount = Number(viewData.extensionAmount || 0);
                  const totalServices = Number(viewData.totalServices || 0);
                  const balanceDue = Number(viewData.balanceDue || 0);

                  // If everything already paid at check-in
                  const isFullyPaidWithoutExtras =
                    bookingAmount === 0 &&
                    extensionAmount === 0 &&
                    totalServices === 0;

                  // Show Receive Cash only if actual pending amount exists
                  const showReceiveCash =
                    balanceDue > 0 && !isFullyPaidWithoutExtras;

                  // Show invoice if fully paid
                  const showInvoice =
                    balanceDue === 0 || isFullyPaidWithoutExtras;

                  return (
                    <>
                      {showReceiveCash && (
                        <Button color="success" onClick={toggleCashModal}>
                          Received Cash
                        </Button>
                      )}

                      {showInvoice && (
                        <Button
                          color="primary"
                          onClick={() => InvoiceDownload(viewData)}
                        >
                          Download Invoice
                        </Button>
                      )}
                    </>
                  );
                })()}

                <Button color="secondary" onClick={toggleViewModal}>
                  Close
                </Button>

              </div>
            </div>
          ) : (
            <p>Loading booking details...</p>
          )}
        </ModalBody>
      </Modal>

      {/* Cash Received Confirmation Modal */}
      <Modal isOpen={cashModal} toggle={toggleCashModal} centered>
        <ModalHeader toggle={toggleCashModal}>Confirm Cash Received</ModalHeader>
        <ModalBody className="text-center">
          <p>Are you sure you have received the cash payment for this booking?</p>
          <div className="d-flex justify-content-center gap-2 mt-3">
            <Button color="success" onClick={handleCashReceived}>Received</Button>
            <Button color="secondary" onClick={toggleCashModal}>Cancel</Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ManageCheckout;
